import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import Button, { buttonClasses } from '../components/ui/Button'
import Input from '../components/ui/Input'
import texts from '../texts'
import { getAuthConfig, requestCode, setAuthMarker, verifyCode } from '../lib/api'
import { browserSupportsWebAuthn, loginWithPasskey, PASSKEY_FLAG_KEY } from '../lib/passkeys'
import { TURNSTILE_SITE_KEY, useTurnstile } from '../lib/useTurnstile'

const CODE_LENGTH = 6
const PENDING_INVITE_KEY = 'wb-pending-invite'

interface LoginProps {
  setAuth: (value: boolean) => void
}

type Step = 'email' | 'code'

function Login({ setAuth }: LoginProps) {
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [newUser, setNewUser] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [googleEnabled, setGoogleEnabled] = useState(false)
  const [passkeyLoginAvailable, setPasskeyLoginAvailable] = useState(false)
  const turnstile = useTurnstile(step === 'email')
  const codeInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    getAuthConfig()
      .then((config) => {
        setGoogleEnabled(config.googleEnabled)
        setPasskeyLoginAvailable(
          config.passkeysEnabled && browserSupportsWebAuthn() && localStorage.getItem(PASSKEY_FLAG_KEY) === '1'
        )
      })
      .catch(() => {
        // Login virker stadig med kode, selv uden auth-config
      })
  }, [])

  const goAfterLogin = () => {
    const pendingInvite = localStorage.getItem(PENDING_INVITE_KEY)
    if (pendingInvite) {
      localStorage.removeItem(PENDING_INVITE_KEY)
      navigate(`/j/${pendingInvite}`)
    } else {
      navigate('/')
    }
  }

  const handleRequestCode = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (TURNSTILE_SITE_KEY && !turnstile.token) {
      setError(texts.login.turnstileWait)
      return
    }
    setBusy(true)
    try {
      const response = await requestCode(email, turnstile.token)
      setNewUser(response.newUser)
      setCode('')
      setStep('code')
      window.setTimeout(() => codeInputRef.current?.focus(), 50)
    } catch (err: any) {
      setError(err.response?.data?.message || texts.login.genericSendError)
      turnstile.reset()
    } finally {
      setBusy(false)
    }
  }

  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (newUser && name.trim().length < 1) {
      setError(texts.login.missingName)
      return
    }
    setBusy(true)
    try {
      const response = await verifyCode(email, code, name)
      setAuthMarker(response.userId, response.name)
      setAuth(true)
      goAfterLogin()
    } catch (err: any) {
      if (err.response?.data?.needName) setNewUser(true)
      setError(err.response?.data?.message || texts.login.genericLoginError)
    } finally {
      setBusy(false)
    }
  }

  const handlePasskeyLogin = async () => {
    setError('')
    setBusy(true)
    try {
      await loginWithPasskey()
      setAuth(true)
      goAfterLogin()
    } catch {
      setError(texts.login.genericLoginError)
    } finally {
      setBusy(false)
    }
  }

  if (step === 'email') {
    return (
      <Screen bare center>
        <div className="grid gap-3.5 text-center">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-[#f5c451] text-[1.4rem] font-extrabold text-ink">
            W
          </span>
          <h1>{texts.login.title}</h1>
          <p className="text-text-2">{texts.login.intro}</p>
        </div>

        <form className="grid gap-3.5" onSubmit={handleRequestCode}>
          <Input
            type="email"
            inputMode="email"
            placeholder={texts.login.emailPlaceholder}
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {TURNSTILE_SITE_KEY && <div ref={turnstile.ref} />}

          {error && <p className="text-red">{error}</p>}

          <Button type="submit" full disabled={busy}>
            {busy ? texts.login.sending : texts.login.sendCode}
          </Button>
        </form>

        <div className="flex items-center gap-2.5 text-[0.8em] text-text-2 before:h-px before:flex-1 before:bg-text-2/20 before:content-[''] after:h-px after:flex-1 after:bg-text-2/20 after:content-['']">
          {texts.login.or}
        </div>

        {googleEnabled && (
          <a href="/api/auth/google" className={buttonClasses({ variant: 'ghost', full: true })}>
            <span className="font-extrabold">G</span> {texts.login.google}
          </a>
        )}

        {passkeyLoginAvailable && (
          <Button variant="ghost" full onClick={handlePasskeyLogin} disabled={busy}>
            ☺ {texts.login.passkey}
          </Button>
        )}

        <p className="text-center text-[0.8rem] text-text-2">{texts.login.protectedBy}</p>
        <a href="/" className="text-center text-[0.8rem] text-text-2">
          {texts.login.backToStart}
        </a>
      </Screen>
    )
  }

  return (
    <Screen bare center>
      <div className="grid gap-3.5 text-center">
        <h1>{texts.login.checkMail}</h1>
        <p className="text-text-2">
          {texts.login.codeSentTo}
          <br />
          <strong className="text-text">{email}</strong>
        </p>
      </div>

      <form className="grid gap-3.5" onSubmit={handleVerifyCode}>
        <div className="relative">
          <div className="flex justify-center gap-2">
            {Array.from({ length: CODE_LENGTH }, (_, i) => (
              <span
                key={i}
                className={`wb-code-box grid h-[54px] w-11 place-items-center rounded-xl border font-mono text-[1.4rem] font-semibold ${
                  i === code.length ? 'border-gold' : 'border-text-2/20'
                }`}
              >
                {code[i] || ''}
              </span>
            ))}
          </div>
          <input
            ref={codeInputRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
            aria-label={texts.login.checkMail}
            className="absolute inset-0 h-full w-full cursor-default opacity-0"
          />
        </div>

        {newUser && (
          <div className="grid gap-1.5">
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
              {texts.login.namePrompt}
            </span>
            <Input
              placeholder={texts.login.namePlaceholder}
              autoComplete="name"
              maxLength={60}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        )}

        {error && <p className="text-red">{error}</p>}

        <Button type="submit" full disabled={busy || code.length !== CODE_LENGTH}>
          {busy ? texts.login.loggingIn : newUser ? texts.login.createAndLogIn : texts.login.logIn}
        </Button>

        <button
          type="button"
          className="text-center text-[0.85em] text-text-2"
          onClick={() => {
            setStep('email')
            setError('')
            turnstile.reset()
          }}
        >
          {texts.login.resendHint}
        </button>
      </form>
    </Screen>
  )
}

export default Login
